import { Injectable } from "@angular/core";
import {
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
  UrlTree,
  CanActivateChild,
} from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { JwtAuthService } from "../services/auth/jwt-auth.service";
import { AppConfirmService } from "../services/app-confirm/app-confirm.service";
import { BlackListService } from "../services/blacklist.service";
import { BlackList } from "../interfaces/blacklist";
import { AssinaturaBannerService } from "../services/assinatura-banner.service";

@Injectable({ providedIn: "root" })
export class UserRoleGuard implements CanActivate, CanActivateChild {
  private _notified = new Set<string>();
  private _lastUserName: string | null = null;

  constructor(
    private router: Router,
    private jwtAuth: JwtAuthService,
    private blackListService: BlackListService,
    private assinaturaBannerService: AssinaturaBannerService,
    private appConfirmService: AppConfirmService
  ) {
    // limpa notificações quando o usuário muda (ex.: logout/login)
    this.jwtAuth.item$.pipe(take(1)).subscribe((item) => {
      this._lastUserName = item?.userName ?? null;
    });

    // observa mudanças de usuário e reseta notificações ao trocar de conta
    this.jwtAuth.item$.subscribe((item) => {
      const userName = item?.userName ?? null;
      if (this._lastUserName && this._lastUserName !== userName) {
        this._notified.clear();
      }
      this._lastUserName = userName;
    });
  }

  private _notifyKey(filialId: string, tipo: string) {
    return `${filialId}:${tipo}`;
  }

  // canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
  // var user = this.jwtAuth.getUser();
  // var roles = this.jwtAuth.item$.value.roles;
  // console.log('ROLES USER:' + roles);
  // console.log('ROLES ROUTE:' + route.data.roles);

  // if (
  //   user &&
  //   route.data &&
  //   route.data.roles &&
  //   route.data.roles.some(r => roles.includes(r))
  // ) {

  //   return true;
  // } else {
  //   this.appConfirmService.confirm({title: 'A T E N Ç Ã O !', message:
  //   'Não Autorizado!Você não tem autorização p/ fazer esta operação, contate o Administrador!', error: true})
  // console.log('Não Autorizado!Você não tem autorização p/ fazer esta operação, contate o Administrador!');
  //   return false;
  // }
  // }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.canActivate(route, state);
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const user = this.jwtAuth.getUser();
    const roles: string[] = this.jwtAuth.item$?.value?.roles ?? [];
    const requiredRoles: string[] | undefined = route.data?.roles;

    if (requiredRoles && !(user && requiredRoles.some((r) => roles.includes(r)))) {
      this.appConfirmService
        .confirm({
          title: "A T E N Ç Ã O !",
          message:
            "Não Autorizado! Você não tem autorização p/ fazer esta operação, contate o Administrador!",
          error: true,
        })
        .subscribe();
      return of(false);
    }

    return this.jwtAuth.itemRemoto$.pipe(
      take(1),
      switchMap((itemRemoto) => {
        const item = this.jwtAuth.item$?.value ?? null;
        const itemAutenticado = itemRemoto ?? item;

        if (!itemAutenticado) {
          return of(true);
        }

        const filialCodigo = (itemAutenticado.filialCodigo ?? "")
          .toString()
          .replace(/\D/g, "");
        const filialId = itemAutenticado.filialId ?? item?.filialId ?? null;
        const tokenBase = item?.token ?? null;
        const userName = item?.userName ?? null;
        const cnpjOuCpf = this.resolveDocumentoConsulta(itemAutenticado, item);

        if (!filialId) {
          this.appConfirmService
            .confirm({
              title: "Filial não encontrada",
              message:
                "Não foi possível determinar a filial associada ao seu usuário. Contate o administrador.",
              error: true,
            })
            .subscribe();
          return of(this.router.parseUrl("/sessions/signin"));
        }

        const payload: Partial<BlackList> = {
          codigo: cnpjOuCpf || filialCodigo,
          desativado: false,
          nome: `${item?.empresaRemota?.nomeFantasia ?? ""}`,
          ultimoLoginUserName: userName,
        };

        return this.blackListService.saveOrUpdateLog(payload as BlackList, tokenBase).pipe(
          take(1),
          switchMap((result: any) => {
            const assinaturaEncontrada = !!result?.assinaturaEncontrada;
            const assinaturaStatus = (result?.assinaturaStatus || "").toString().toLowerCase();

            if (assinaturaEncontrada) {
              this.assinaturaBannerService.syncFromBlacklist(result);
            } else {
              this.assinaturaBannerService.clear();
            }

            if (!assinaturaEncontrada && result?.onBlackList) {
              this.appConfirmService
                .confirm({
                  title: "Acesso Bloqueado ou Licença Expirada",
                  message:
                    "Seu acesso ao Infokaw está bloqueado ou a licença expirou. Para continuar utilizando nossos serviços, regularize sua situação entrando em contato com o suporte. Estamos prontos para ajudar e esclarecer qualquer dúvida.",
                  error: true,
                  icon: "report_problem",
                })
                .subscribe();
              return of(this.router.parseUrl("/sessions/signin"));
            }

            if (result?.vencimento) {
              const dias = this._diasRestantes(result.vencimento);
              const key = this._notifyKey(String(filialId), "vencimento");
              if (dias <= 5 && !this._notified.has(key)) {
                this._notified.add(key);
                this.appConfirmService
                  .confirm({
                    title: "Atenção: Licença Próxima do Vencimento",
                    message: `Olá! Seu acesso ao Infokaw irá expirar em ${dias} dia(s). Caso ainda não tenhamos entrado em contato sobre a renovação, fique à vontade para nos chamar a qualquer momento. Estamos aqui para ajudar na regularização do novo período e esclarecer qualquer dúvida. Conte conosco!`,
                    error: true,
                    icon: "report_problem",
                  })
                  .subscribe();
              }
            }

            // Verifica assinatura inadimplente ANTES de verificar títulos vencidos
            if (result?.assinaturaInadimplente && (user?.usuario ?? "").toLowerCase() !== "root") {
              const keyAssinatura = this._notifyKey(String(filialId), "assinatura");
              if (!this._notified.has(keyAssinatura)) {
                this._notified.add(keyAssinatura);
                this.appConfirmService
                  .confirm({
                    title: "Acesso Bloqueado - Assinatura Com Problema",
                    message:
                      (result.assinaturaMensagem || "Problema detectado na assinatura") +
                      "\n\nPara restabelecer o acesso, regularize sua assinatura. Em caso de dúvidas, entre em contato:\n\n" +
                      "Email: suporte@infokaw.com.br\n" +
                      "Fone: (44)99942-2829",
                    error: true,
                    icon: "report_problem",
                  })
                  .subscribe();
              }
              return of(this.router.parseUrl("/sessions/signin"));
            }

            return this.blackListService.getVencidos(cnpjOuCpf).pipe(
              take(1),
              map((titulos: any[]) => {
                if (!titulos || !Array.isArray(titulos) || titulos.length === 0) {
                  return true;
                }

                const titulosVencidos = titulos.map((titulo) => ({
                  valorSaldo: titulo.valorSaldo,
                  valorJurosmora: titulo.valorJurosmora,
                  valorAcrescimo: titulo.valorAcrescimo,
                  valorDesconto: titulo.valorDesconto,
                  vcto: titulo.vcto,
                }));

                if (result?.admUser) {
                  const admUsers = (result.admUser as string)
                    .split(",")
                    .map((u: string) => u.trim().toLowerCase());

                  if (admUsers.includes((userName ?? "").toLowerCase())) {
                    const notifyKey = this._notifyKey(String(filialId), "titulos");
                    if (!this._notified.has(notifyKey)) {
                      this._notified.add(notifyKey);
                      this.appConfirmService
                        .confirm({
                          title: "** C O M U N I C A D O **",
                          message:
                            "Olá! Identificamos que existem títulos vencidos há mais de 5 dias para a empresa. Por favor, quando possível, regularize sua situação para evitar eventuais transtornos. Caso precise de qualquer auxílio, nossa equipe está à disposição para ajudar. Muito obrigado!",
                          error: true,
                          icon: "report_problem",
                        })
                        .subscribe();
                    }
                  }
                }

                if ((user?.usuario ?? "").toLowerCase() !== "root") {
                  const getValorAtualizado = (t: any): number => {
                    const saldo = Number(t.valorSaldo) || 0;
                    const juros = Number(t.valorJurosmora) || 0;
                    const acrescimo = Number(t.valorAcrescimo) || 0;
                    const desconto = Number(t.valorDesconto) || 0;
                    return saldo + juros + acrescimo - desconto;
                  };

                  const totalSaldo = titulosVencidos.reduce(
                    (sum, t) => sum + getValorAtualizado(t),
                    0
                  );
                  const titulosList = titulosVencidos
                    .map((t, idx) => {
                      const vctoDate = new Date(t.vcto);
                      const vctoFormatted = vctoDate.toLocaleDateString("pt-BR");
                      const valorAtualizado = getValorAtualizado(t);
                      return `#${idx + 1}: Vencimento: ${vctoFormatted}, Valor: R$ ${Number(
                        valorAtualizado
                      ).toFixed(2)}`;
                    })
                    .join("\n");

                  this.appConfirmService
                    .confirm({
                      title: "Acesso Bloqueado por Títulos Vencidos",
                      message:
                        `Seu acesso ao Infokaw foi bloqueado automaticamente devido à existência de títulos vencidos há 5 dias ou mais em seu cadastro.\n\n` +
                        `Títulos vencidos:\n${titulosList}\n\n` +
                          `Valor total atualizado em aberto: R$ ${totalSaldo.toFixed(2)}.\n\n` +
                        `Para restabelecer o acesso, regularize sua situação financeira. Em caso de dúvidas ou necessidade de suporte, nossa equipe está à disposição para ajudar. Agradecemos pela compreensão.`,
                      error: true,
                      icon: "report_problem",
                    })
                    .subscribe();

                  return this.router.parseUrl("/sessions/signin");
                }

                return true;
              }),
              catchError((err) => {
                console.error("guard getVencidos erro", err);
                return of(true);
              })
            );
          }),
          catchError((err) => {
            console.error("guard saveOrUpdateLog erro", err);
            return of(true);
          })
        );
      }),
      catchError((err) => {
        console.error("UserRoleGuard erro geral", err);
        return of(true);
      })
    );
  }

  private _diasRestantes(vencimentoIsoOrDate: string | Date): number {
    const oneDay = 1000 * 60 * 60 * 24;
    const end = new Date(vencimentoIsoOrDate);
    const today = new Date();
    end.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return Math.round((end.getTime() - today.getTime()) / oneDay);
  }

  private resolveDocumentoConsulta(itemRemoto: any, item: any): string {
    const fromFilialCodigo = `${itemRemoto?.filialCodigo ?? ""}`.replace(/\D/g, "");
    if (fromFilialCodigo.length === 11 || fromFilialCodigo.length === 14) {
      return fromFilialCodigo;
    }

    const fromEmpresaRemota = `${item?.empresaRemota?.inscricaoFederal ?? ""}`.replace(/\D/g, "");
    if (fromEmpresaRemota.length === 11 || fromEmpresaRemota.length === 14) {
      return fromEmpresaRemota;
    }

    return fromFilialCodigo || fromEmpresaRemota;
  }
}